import { useState, useEffect, useCallback, useMemo } from "react";
import { authFetch } from "@/lib/authFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// FIN-6 — useFinanceAssinaturas.
//
// Agregador da Assinaturas tab:
//   GET /api/subscriptions          (lista + cálculos derivados)
//   GET /api/subscriptions/summary  (totais, by_category, next_billing)
//   GET /api/subscriptions/detect   (lazy load — só ao abrir SugestoesModal)
//
// Filtros client-side: statusFilter (Todas/Ativas/Pausadas/Canceladas)
// + categoryFilter (multi-select).
//
// CRUD: createSubscription / updateSubscription / deleteSubscription.
// addFromSuggestion: cria a partir de uma sugestão do detect e remove
// da lista local (optimistic).

const STATUS_FILTERS = ["Todas", "Ativas", "Pausadas", "Canceladas"];
const STATUS_TO_API = {
  "Ativas": "active", "Pausadas": "paused", "Canceladas": "canceled",
};

async function _safeJson(url) {
  try {
    const r = await authFetch(url);
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      return { error: detail?.detail || `HTTP ${r.status}`, data: null };
    }
    return { error: null, data: await r.json() };
  } catch (e) {
    return { error: String(e.message || e), data: null };
  }
}

export default function useFinanceAssinaturas() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("Todas");
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const [a, s] = await Promise.all([
      _safeJson(`${API_URL}/api/subscriptions`),
      _safeJson(`${API_URL}/api/subscriptions/summary`),
    ]);
    if (a.data) setSubscriptions(Array.isArray(a.data?.subscriptions) ? a.data.subscriptions : []);
    if (s.data) setSummary(s.data);
    const errs = [a.error, s.error].filter(Boolean);
    if (errs.length) setError(errs[0]);
    setIsLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Filtros client-side
  const filteredSubscriptions = useMemo(() => {
    let list = subscriptions;
    if (statusFilter && statusFilter !== "Todas") {
      const apiStatus = STATUS_TO_API[statusFilter];
      list = list.filter(s => s.status === apiStatus);
    }
    if (categoryFilter && categoryFilter.length > 0) {
      const set = new Set(categoryFilter);
      list = list.filter(s => set.has(s.category));
    }
    return list;
  }, [subscriptions, statusFilter, categoryFilter]);

  const knownCategories = useMemo(() => {
    const s = new Set();
    subscriptions.forEach(sub => { if (sub.category) s.add(sub.category); });
    return Array.from(s).sort();
  }, [subscriptions]);

  // CRUD
  const createSubscription = useCallback(async (payload) => {
    const r = await authFetch(`${API_URL}/api/subscriptions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      throw new Error(typeof detail?.detail === "string" ? detail.detail : `HTTP ${r.status}`);
    }
    const fresh = await r.json();
    await fetchAll();
    return fresh;
  }, [fetchAll]);

  const updateSubscription = useCallback(async (id, patch) => {
    const r = await authFetch(`${API_URL}/api/subscriptions/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      throw new Error(typeof detail?.detail === "string" ? detail.detail : `HTTP ${r.status}`);
    }
    const fresh = await r.json();
    setSubscriptions(prev => prev.map(s => (s.id === id ? { ...s, ...fresh } : s)));
    fetchAll();
    return fresh;
  }, [fetchAll]);

  const deleteSubscription = useCallback(async (id) => {
    const r = await authFetch(`${API_URL}/api/subscriptions/${id}`, { method: "DELETE" });
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      throw new Error(typeof detail?.detail === "string" ? detail.detail : `HTTP ${r.status}`);
    }
    await fetchAll();
  }, [fetchAll]);

  const detectSuggestions = useCallback(async () => {
    setIsLoadingSuggestions(true);
    const r = await _safeJson(`${API_URL}/api/subscriptions/detect`);
    if (r.data) setSuggestions(Array.isArray(r.data?.suggestions) ? r.data.suggestions : []);
    setIsLoadingSuggestions(false);
    return r.data?.suggestions || [];
  }, []);

  const addFromSuggestion = useCallback(async (suggestion) => {
    const payload = {
      name: suggestion.suggested_name || suggestion.merchant,
      merchant: suggestion.merchant,
      amount: suggestion.amount,
      frequency: suggestion.frequency,
      billing_day: suggestion.billing_day,
      category: suggestion.category_guess || null,
    };
    const fresh = await createSubscription(payload);
    // Remove da lista de sugestões
    setSuggestions(prev => prev.filter(s => s.merchant !== suggestion.merchant));
    return fresh;
  }, [createSubscription]);

  const exportCsv = useCallback(async () => {
    const r = await authFetch(`${API_URL}/api/subscriptions/export?format=csv`);
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      throw new Error(detail?.detail || `HTTP ${r.status}`);
    }
    const blob = await r.blob();
    const cd = r.headers.get("Content-Disposition") || "";
    const m = /filename="([^"]+)"/.exec(cd);
    const filename = m ? m[1] : `assinaturas_${new Date().toISOString().slice(0, 10)}.csv`;
    const a = document.createElement("a");
    const url = window.URL.createObjectURL(blob);
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    return { filename, size: blob.size };
  }, []);

  return {
    subscriptions,
    filteredSubscriptions,
    summary,
    suggestions,
    statusFilter,
    setStatusFilter,
    categoryFilter,
    setCategoryFilter,
    statusOptions: STATUS_FILTERS,
    knownCategories,
    isLoading,
    isLoadingSuggestions,
    error,
    refetch: fetchAll,
    createSubscription,
    updateSubscription,
    deleteSubscription,
    detectSuggestions,
    addFromSuggestion,
    exportCsv,
  };
}
